import React, { useState } from 'react';
import { useCart } from '../../context/CartContext';
import { apiService } from '../../services/api';
import { districts } from '../../data/content';
import Toast from '../../components/Toast';
import './CartPage.css';

function CartPage() {
  const {
    cartItems,
    removeFromCart,
    incrementQuantity,
    decrementQuantity,
    getTotalPrice,
    getDeliveryPrice,
    getTotalWithDelivery,
    clearCart,
  } = useCart();

  const [showOrderForm, setShowOrderForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    name: '', address: '', area: '', phone: '', phone2: '', message: '', paymentMethod: 'Cash on Delivery',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.address.trim() || !formData.phone.trim() || !formData.area) {
      setToast({ message: 'Please fill in Name, Address, Area and Phone Number', type: 'error' });
      return;
    }

    setIsSubmitting(true);

    // Calculate next day for delivery (or same day if early morning)
    const deliveryDate = new Date();
    deliveryDate.setDate(deliveryDate.getDate() + 1);

    const orderData = {
      customerName: formData.name.trim(),
      email: '',
      phone1: formData.phone.trim(),
      phone2: formData.phone2.trim(),
      deliveryType: 'Home Delivery',
      paymentMethod: formData.paymentMethod,
      specialInstructions: formData.message.trim(),
      deliveryDate: deliveryDate.toISOString(),
      address: formData.address.trim(),
      area: formData.area.trim(),
      subtotal: getTotalPrice(),
      deliveryCharge: getDeliveryPrice(),
      totalPrice: getTotalWithDelivery(),
      items: cartItems.map((item) => ({
        productId: item.id,
        productName: item.title,
        quantity: item.quantity,
        price: item.price,
      })),
    };

    try {
      const response = await apiService.createOrder(orderData);
      setToast({ 
        message: 'Order placed successfully! Order ID: ' + response.id + '. We will contact you soon. ✓', 
        type: 'success' 
      });
      window.scrollTo(0, 0);
      clearCart();
      setShowOrderForm(false);
      setFormData({ name: '', address: '', area: '', phone: '', phone2: '', message: '', paymentMethod: 'Cash on Delivery' });
    } catch (error) {
      console.error('Error placing order:', error);
      setToast({ message: 'Error placing order. Please try again.', type: 'error' });
    }

    setIsSubmitting(false);
  };

  const cancelOrder = () => {
    setShowOrderForm(false);
    setToast({ message: 'Order cancelled', type: 'info' });
  };

  /* ── Empty state ── */
  if (cartItems.length === 0) {
    return (
      <div className="cart-page-wrapper page-transition-wrapper">
        <div className="cart-hero">
          <span className="cart-hero-kicker">Your Cart</span>
          <h1>Shopping <em>Cart</em></h1>
        </div>

        <div className="cart-container">
          <div className="empty-cart">
            <span className="empty-cart-icon">🛒</span>
            <h2>Your Cart is Empty</h2>
            <p>Start shopping to add items to your cart.</p>
            <a href="/more" className="continue-shopping-btn">Browse Products</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page-wrapper page-transition-wrapper">

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* ── Hero strip ── */}
      <div className="cart-hero">
        <span className="cart-hero-kicker">Your Cart</span>
        <h1>Shopping <em>Cart</em></h1>
      </div>

      {/* ── Main content ── */}
      <div className="cart-container">

        <div className="cart-header">
          <h1>{cartItems.length} Item{cartItems.length !== 1 ? 's' : ''} in Cart</h1>
          <button className="clear-cart-btn" onClick={clearCart}>✕ Clear Cart</button>
        </div>

        <div className="cart-content">

          {/* ── Items ── */}
          <div className="cart-items">
            {cartItems.map((item) => (
              <div key={item.id} className="cart-item-card">
                <div className="item-info-section">
                  <h3 className="item-name">{item.title}</h3>
                  <p className="item-unit-price">Unit Price: <span>Rs. {item.price.toLocaleString()}</span></p>
                </div>

                <div className="item-controls-section">
                  <div className="quantity-section">
                    <span className="qty-label">Quantity</span>
                    <div className="quantity-control">
                      <button className="qty-btn qty-minus" onClick={() => decrementQuantity(item.id)}>−</button>
                      <span className="qty-display">{item.quantity}</span>
                      <button className="qty-btn qty-plus" onClick={() => incrementQuantity(item.id)}>+</button>
                    </div>
                  </div>

                  <div className="pricing-section">
                    <span className="calc-label">Subtotal</span>
                    <span className="item-total">Rs. {(item.price * item.quantity).toLocaleString()}</span>
                  </div>

                  <button className="remove-btn" onClick={() => removeFromCart(item.id)}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* ── Summary ── */}
          <div className="cart-summary">
            <div className="summary-section">
              <h3>Order Summary</h3>

              <div className="summary-row">
                <span>Subtotal</span>
                <span>Rs. {getTotalPrice().toLocaleString()}</span>
              </div>

              <div className="summary-row">
                <span>Delivery</span>
                <span className="delivery-charge">Rs. {getDeliveryPrice().toLocaleString()}
                {getDeliveryPrice() === 0 && <small className="delivery-note"> (Free with Combo Packs)</small>}
                </span>
              </div>

              <div className="summary-row total">
                <span>Total</span>
                <span>Rs. {getTotalWithDelivery().toLocaleString()}</span>
              </div>

              <button className="order-btn" onClick={() => setShowOrderForm(true)}>
                Place Order →
              </button>

              <a href="/more" className="continue-shopping-link">Continue Shopping</a>
            </div>

            <div className="promo-section">
              <h4>Promo Code</h4>
              <div className="promo-input">
                <input type="text" placeholder="Enter promo code" />
                <button>Apply</button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Order Modal ── */}
      {showOrderForm && (
        <div className="modal-overlay" onClick={cancelOrder}>
          <div className="order-modal" onClick={(e) => e.stopPropagation()}>

            <button className="modal-close" onClick={cancelOrder} aria-label="Close">✕</button>
            <h2>Complete Your Order</h2>

            <form onSubmit={handleOrderSubmit} className="order-form">

              {/* Delivery info */}
              <div className="form-section">
                <h3>Delivery Information</h3>

                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input type="text" id="name" name="name" value={formData.name}
                    onChange={handleInputChange} placeholder="Enter your full name" required />
                </div>

                <div className="form-group">
                  <label htmlFor="address">Delivery Address *</label>
                  <textarea id="address" name="address" value={formData.address}
                    onChange={handleInputChange} placeholder="Enter your delivery address" rows="3" required />
                </div>

                <div className="form-group">
                  <label htmlFor="area">Delivery Area *</label>
                  <select id="area" name="area" value={formData.area}
                    onChange={handleInputChange} required>
                    <option value="">Select your area</option>
                    {districts.map((district) => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input type="tel" id="phone" name="phone" value={formData.phone}
                    onChange={handleInputChange} placeholder="Enter your phone number" required />
                </div>

                <div className="form-group">
                  <label htmlFor="phone2">Alternative Phone (Optional)</label>
                  <input type="tel" id="phone2" name="phone2" value={formData.phone2}
                    onChange={handleInputChange} placeholder="Alternative phone number" />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Special Instructions (Optional)</label>
                  <textarea id="message" name="message" value={formData.message}
                    onChange={handleInputChange} placeholder="Any special requests or instructions?" rows="2" />
                </div>

                <div className="form-group">
                  <label>Payment Method *</label>
                  <div className="payment-options">
                    <label className="payment-option">
                      <input type="radio" name="paymentMethod" value="Cash on Delivery"
                        checked={formData.paymentMethod === 'Cash on Delivery'}
                        onChange={handleInputChange} required />
                      <span className="radio-label">💵 Cash on Delivery</span>
                    </label>
                    <label className="payment-option">
                      <input type="radio" name="paymentMethod" value="Card Payment"
                        checked={formData.paymentMethod === 'Card Payment'}
                        onChange={handleInputChange} required />
                      <span className="radio-label">💳 Card Payment</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Order summary */}
              <div className="form-section">
                <h3>Order Summary</h3>

                <div className="order-items-summary">
                  {cartItems.map((item) => (
                    <div key={item.id} className="summary-item">
                      <div className="summary-item-name">
                        <span>{item.title}</span>
                        <span className="summary-qty">×{item.quantity}</span>
                      </div>
                      <span className="summary-price">Rs. {(item.price * item.quantity).toLocaleString()}</span>
                    </div>
                  ))}
                </div>

                <div className="summary-divider" />

                <div className="summary-totals">
                  <div className="summary-total-row">
                    <span>Subtotal</span>
                    <span>Rs. {getTotalPrice().toLocaleString()}</span>
                  </div>
                  <div className="summary-total-row">
                    <span>Delivery</span>
                    <span>Rs. {getDeliveryPrice().toLocaleString()}</span>
                  </div>
                  <div className="summary-total-row final">
                    <span>Total</span>
                    <span>Rs. {getTotalWithDelivery().toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={cancelOrder}>Cancel</button>
                <button type="submit" className="btn-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Processing...' : 'Confirm & Place Order'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default CartPage;